import { MonitorPlay, Zap } from 'lucide-react'
import type { InvestigationAction, InvestigationActionResult } from '../InvestigationSidebar'

// Browser Load (Proxied / Direct) for the InvestigationSidebar, shared by
// CaseEntryDetailPage and the Workbench (AdminSubmitEntryView).
//
// /probedomain[proxy] only answers 202 "Analysis started": python runs the
// WHOIS + browser pass + content analysis in a background thread and records
// the outcome as user_entry_pipeline_steps rows — "manual_probe_domain
// started", then "... finished" or "... failed" (a browser timeout/failure is
// "failed"; a run whose API process died is swept to "failed" on the next
// boot/probe, or after 30 min). awaitOutcome polls those rows so the sidebar
// keeps spinning until the run really ends, instead of showing the 202 as a
// green success while the run died behind it.

type Step = { step: string; created_at: string; json_info?: unknown }

const STARTED = 'manual_probe_domain started'
const ENDED = new Set(['manual_probe_domain finished', 'manual_probe_domain failed'])
const POLL_MS = 5_000
// The run's own "started" row is written right after the 202 (behind a quick
// sweep of lost runs); not seeing it at all means the run never began.
const START_GRACE_MS = 2 * 60_000
// Past python's 30-min orphan sweep, so a lost run normally reports itself
// as failed before this gives up on it.
const GIVE_UP_MS = 35 * 60_000

// run() result → number of "started" rows that existed before that click,
// so awaitOutcome knows which started row is this run's.
const startedBefore = new WeakMap<object, number>()

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

async function fetchProbeSteps(userEntryId: string): Promise<Step[] | null> {
  try {
    const res = await fetch(`/api/entries/entry-detail?user_entry_id=${encodeURIComponent(userEntryId)}`)
    const result: { success: boolean; data?: { entry?: { user_entry_pipeline_steps?: Step[] } | null } } = await res.json()
    if (!result.success) return null
    return (result.data?.entry?.user_entry_pipeline_steps ?? [])
      .filter((s) => s.step === STARTED || ENDED.has(s.step))
      .sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at))
  } catch {
    return null
  }
}

function info(step: Step): Record<string, unknown> {
  const raw = step.json_info
  if (typeof raw === 'string') {
    try { return JSON.parse(raw) } catch { return { value: raw } }
  }
  return (raw ?? {}) as Record<string, unknown>
}

async function awaitProbeOutcome(userEntryId: string, accepted: InvestigationActionResult): Promise<InvestigationActionResult> {
  const before = startedBefore.get(accepted) ?? 0
  const t0 = Date.now()
  for (;;) {
    await sleep(POLL_MS)
    const steps = await fetchProbeSteps(userEntryId)
    const elapsed = Date.now() - t0
    if (steps) {
      const started = steps.filter((s) => s.step === STARTED)
      const mine = started[before]
      if (mine) {
        const end = steps.find((s) => ENDED.has(s.step) && Date.parse(s.created_at) >= Date.parse(mine.created_at))
        if (end) {
          const details = info(end)
          if (end.step === 'manual_probe_domain finished') return { success: true, data: details }
          const stage = details.stage ? `${details.stage}: ` : ''
          return { success: false, error: `${stage}${details.error ?? 'failed'}`, data: details }
        }
      } else if (elapsed > START_GRACE_MS) {
        return { success: false, error: 'The run never started (no "manual_probe_domain started" step) — the API may have restarted before it began.' }
      }
    }
    if (elapsed > GIVE_UP_MS) {
      return { success: false, error: `No result after ${Math.round(GIVE_UP_MS / 60_000)} min — the run was most likely lost (API restart). Check the entry's pipeline steps.` }
    }
  }
}

async function startProbe(path: string, target: string, userEntryId: string): Promise<InvestigationActionResult> {
  const before = (await fetchProbeSteps(userEntryId))?.filter((s) => s.step === STARTED).length ?? 0
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ domain: target, userEntryId }),
  })
  const result: InvestigationActionResult = await res.json()
  startedBefore.set(result, before)
  return result
}

/** The two Browser Load actions for `userEntryId` (none yet → they fail
 * fast with a clear message). `onSettled` runs once a run has ended, so the
 * page can refetch the domain/browser rows it wrote. */
export function browserLoadActions(userEntryId: string | null | undefined, onSettled?: () => void): InvestigationAction[] {
  const make = (id: string, label: string, description: string, icon: InvestigationAction['icon'], path: string): InvestigationAction => ({
    id,
    label,
    description,
    icon,
    run: async (target) => {
      if (!userEntryId) return { success: false, error: 'Submit the entry first — the run is logged against its user_entry_id.' }
      return startProbe(path, target, userEntryId)
    },
    awaitOutcome: async (_target, accepted) => {
      const outcome = await awaitProbeOutcome(userEntryId!, accepted)
      onSettled?.()
      return outcome
    },
  })
  return [
    // → /api/entries/probe-domain → Hono /probedomainproxy → python; the
    // browser session goes out through a Webshare proxy.
    make('browser-load-proxied', 'Browser Load (Proxied)', 'Headless render + redirect chain — routed through an outbound proxy', MonitorPlay, '/api/entries/probe-domain'),
    // → /api/entries/probe-domain-direct → Hono /probedomain → python;
    // the render worker fetches the target from its own IP.
    make('browser-load-direct', 'Browser Load (Direct)', 'Headless render + redirect chain — no outbound proxy', Zap, '/api/entries/probe-domain-direct'),
  ]
}
